/**
 * DocumenteInterneTab - Tab pentru gestionarea documentelor interne
 * Categorii custom, subcategorii, control acces, upload documente
 */
import { memo, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, Upload, Search, Download, Plus, Trash2, FolderOpen,
  File, Settings, Users, X, ChevronRight, Edit, Printer, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const DocumenteInterneTab = memo(function DocumenteInterneTab({ hasPermission }) {
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [accessCategory, setAccessCategory] = useState(null);

  // Form states
  const [catName, setCatName] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [newSubName, setNewSubName] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCatId, setUploadCatId] = useState('');
  const [uploadSubId, setUploadSubId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [accessUsers, setAccessUsers] = useState([]);

  // Fetch data
  useEffect(() => {
    fetchCategories();
    fetchDocuments();
    fetchUsers();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/doc-categories`, getAuthHeaders());
      setCategories(res.data);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  };

  const fetchDocuments = async (catId, subId) => {
    try {
      setLoading(true);
      let url = `${API}/api/internal-documents`;
      const params = new URLSearchParams();
      if (catId) params.append('category_id', catId);
      if (subId) params.append('subcategory_id', subId);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, getAuthHeaders());
      setDocuments(res.data);
    } catch (e) {
      console.error('Error fetching documents:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/users`, getAuthHeaders());
      setUsers(res.data.filter(u => u.role !== 'admin'));
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    if (!catName.trim()) return toast.error('Introduceți un nume');
    try {
      if (editingCategory) {
        await axios.put(`${API}/api/doc-categories/${editingCategory.id}`, {
          name: catName,
          subcategories: subcategories
        }, getAuthHeaders());
        toast.success('Categorie actualizată');
      } else {
        await axios.post(`${API}/api/doc-categories`, {
          name: catName,
          subcategories: subcategories
        }, getAuthHeaders());
        toast.success('Categorie creată');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (e) {
      toast.error('Eroare la salvarea categoriei');
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Sigur doriți să ștergeți această categorie și toate documentele aferente?')) return;
    try {
      await axios.delete(`${API}/api/doc-categories/${catId}`, getAuthHeaders());
      toast.success('Categorie ștearsă');
      if (selectedCategory === catId) {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
      }
      fetchCategories();
      fetchDocuments();
    } catch (e) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetCategoryForm = () => {
    setCatName('');
    setSubcategories([]);
    setNewSubName('');
    setEditingCategory(null);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCatName(cat.name);
    setSubcategories(cat.subcategories || []);
    setShowCategoryDialog(true);
  };

  const addSubcategory = () => {
    if (!newSubName.trim()) return;
    setSubcategories(prev => [...prev, { name: newSubName.trim() }]);
    setNewSubName('');
  };

  const removeSubcategory = (idx) => {
    setSubcategories(prev => prev.filter((_, i) => i !== idx));
  };

  // Access control
  const openAccessDialog = (cat) => {
    setAccessCategory(cat);
    setAccessUsers(cat.allowed_users || []);
    setShowAccessDialog(true);
  };

  const handleSaveAccess = async () => {
    try {
      await axios.put(`${API}/api/doc-categories/${accessCategory.id}`, {
        allowed_users: accessUsers
      }, getAuthHeaders());
      toast.success('Acces actualizat');
      setShowAccessDialog(false);
      fetchCategories();
    } catch (e) {
      toast.error('Eroare la salvarea accesului');
    }
  };

  const toggleUserAccess = (userId) => {
    setAccessUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Upload
  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim() || !uploadCatId) {
      return toast.error('Completați toate câmpurile obligatorii');
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', uploadName);
      formData.append('category_id', uploadCatId);
      if (uploadSubId) formData.append('subcategory_id', uploadSubId);
      formData.append('file', uploadFile);

      await axios.post(`${API}/api/internal-documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document încărcat cu succes');
      setShowUploadDialog(false);
      setUploadName('');
      setUploadFile(null);
      setUploadCatId('');
      setUploadSubId('');
      fetchDocuments(selectedCategory, selectedSubcategory);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la încărcare');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/api/internal-documents/${doc.id}/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Eroare la descărcare');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest document?')) return;
    try {
      await axios.delete(`${API}/api/internal-documents/${docId}`, getAuthHeaders());
      toast.success('Document șters');
      fetchDocuments(selectedCategory, selectedSubcategory);
    } catch (e) {
      toast.error('Eroare la ștergere');
    }
  };

  const handlePreviewPrint = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      // Open preview in a new window
      const previewUrl = `${API}/api/internal-documents/${doc.id}/preview`;
      
      // Fetch the preview content with auth
      const res = await axios.get(previewUrl, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        toast.error('Browserul a blocat fereastra popup. Permiteți popup-urile pentru acest site.');
      }
    } catch (e) {
      toast.error('Eroare la previzualizare');
    }
  };

  const selectCategory = (catId) => {
    const newCat = selectedCategory === catId ? null : catId;
    setSelectedCategory(newCat);
    setSelectedSubcategory(null);
    fetchDocuments(newCat, null);
  };

  const selectSubcategory = (subId) => {
    const newSub = selectedSubcategory === subId ? null : subId;
    setSelectedSubcategory(newSub);
    fetchDocuments(selectedCategory, newSub);
  };

  const handleSearch = () => {
    fetchDocuments(selectedCategory, selectedSubcategory);
  };

  const selectedCatObj = categories.find(c => c.id === selectedCategory);

  return (
    <div className="space-y-4" data-testid="documente-interne-container">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-amber-600" />
                Documente Interne
              </CardTitle>
              <CardDescription>
                Gestionează categoriile și documentele interne ale companiei
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }}
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4" />
                Categorie Nouă
              </Button>
              <Button 
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                onClick={() => setShowUploadDialog(true)}
                data-testid="upload-document-btn"
              >
                <Upload className="w-4 h-4" />
                Încarcă Document
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-4 gap-4">
        {/* Left sidebar - Categories */}
        <div className="col-span-1 space-y-2">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Categorii</CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1">
              {categories.length === 0 ? (
                <p className="text-xs text-slate-400 p-2">Nicio categorie. Creați una.</p>
              ) : (
                categories.map(cat => (
                  <div key={cat.id}>
                    <div 
                      className={`flex items-center justify-between p-2 rounded-md cursor-pointer text-sm transition-colors ${
                        selectedCategory === cat.id ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-1" onClick={() => selectCategory(cat.id)}>
                        <FolderOpen className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate font-medium">{cat.name}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => openAccessDialog(cat)} className="p-1 hover:bg-slate-200 rounded" title="Acces utilizatori">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => openEditCategory(cat)} className="p-1 hover:bg-slate-200 rounded" title="Editează">
                          <Edit className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.id)} className="p-1 hover:bg-red-100 rounded" title="Șterge">
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                    {/* Subcategories */}
                    {selectedCategory === cat.id && cat.subcategories?.length > 0 && (
                      <div className="ml-6 mt-1 space-y-0.5">
                        {cat.subcategories.map(sub => (
                          <div 
                            key={sub.id}
                            onClick={() => selectSubcategory(sub.id)}
                            className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer transition-colors ${
                              selectedSubcategory === sub.id ? 'bg-amber-100 text-amber-700' : 'text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            <ChevronRight className="w-3 h-3" />
                            {sub.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Access legend */}
              {categories.some(c => c.allowed_users?.length > 0) && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-400 px-2">
                    <Users className="w-3 h-3 inline mr-1" />
                    = acces restricționat
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right - Documents list */}
        <div className="col-span-3">
          <Card>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  {selectedCatObj ? (
                    <span className="flex items-center gap-1">
                      {selectedCatObj.name}
                      {selectedSubcategory && selectedCatObj.subcategories?.find(s => s.id === selectedSubcategory) && (
                        <>
                          <ChevronRight className="w-4 h-4" />
                          {selectedCatObj.subcategories.find(s => s.id === selectedSubcategory)?.name}
                        </>
                      )}
                    </span>
                  ) : 'Toate documentele'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Caută..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-8 h-8 w-48 text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12 text-slate-400">Se încarcă...</div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12">
                  <File className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 text-sm">Niciun document{selectedCatObj ? ` în "${selectedCatObj.name}"` : ''}</p>
                  <Button 
                    size="sm" 
                    className="mt-3 gap-2 bg-amber-600 hover:bg-amber-700"
                    onClick={() => { setUploadCatId(selectedCategory || ''); setShowUploadDialog(true); }}
                  >
                    <Upload className="w-3 h-3" />
                    Încarcă Document
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Document</TableHead>
                      <TableHead className="font-semibold">Categorie</TableHead>
                      <TableHead className="font-semibold">Dimensiune</TableHead>
                      <TableHead className="font-semibold">Încărcat de</TableHead>
                      <TableHead className="font-semibold">Data</TableHead>
                      <TableHead className="font-semibold text-center w-32">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map(doc => (
                      <TableRow key={doc.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:text-amber-700 transition-colors"
                            onClick={() => handlePreviewPrint(doc)}
                            title="Click pentru previzualizare și printare"
                          >
                            <FileText className={`w-4 h-4 flex-shrink-0 ${doc.file_type?.includes('pdf') ? 'text-red-500' : doc.file_type?.includes('image') ? 'text-green-500' : 'text-blue-500'}`} />
                            <span className="font-medium text-sm underline decoration-dotted">{doc.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{doc.category_name}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{doc.file_size}</TableCell>
                        <TableCell className="text-sm text-slate-500">{doc.uploaded_by}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(doc.uploaded_at).toLocaleDateString('ro-RO')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePreviewPrint(doc)} title="Previzualizare & Printare" data-testid="print-doc-btn">
                              <Printer className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(doc)} title="Descarcă" data-testid="download-doc-btn">
                              <Download className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteDocument(doc.id)} title="Șterge" data-testid="delete-doc-btn">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* === DIALOG: Create/Edit Category === */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => { if (!open) resetCategoryForm(); setShowCategoryDialog(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editează Categorie' : 'Categorie Nouă'}</DialogTitle>
            <DialogDescription>Creați categorii și subcategorii pentru organizarea documentelor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume categorie *</Label>
              <Input 
                value={catName} 
                onChange={(e) => setCatName(e.target.value)} 
                placeholder="Ex: Contracte"
                data-testid="category-name-input"
              />
            </div>
            
            <div>
              <Label>Subcategorii</Label>
              <div className="space-y-2 mt-1">
                {subcategories.map((sub, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input value={sub.name} readOnly className="flex-1 bg-slate-50 h-8 text-sm" />
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeSubcategory(idx)}>
                      <X className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Input 
                    value={newSubName} 
                    onChange={(e) => setNewSubName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSubcategory()}
                    placeholder="Nume subcategorie..."
                    className="flex-1 h-8 text-sm"
                    data-testid="subcategory-name-input"
                  />
                  <Button variant="outline" size="sm" className="h-8" onClick={addSubcategory}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { resetCategoryForm(); setShowCategoryDialog(false); }}>Anulează</Button>
              <Button onClick={handleSaveCategory} className="bg-amber-600 hover:bg-amber-700" data-testid="save-category-btn">
                {editingCategory ? 'Salvează' : 'Creează'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === DIALOG: Upload Document === */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Încarcă Document</DialogTitle>
            <DialogDescription>Încărcați un document intern. Max 10MB. PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT, CSV.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume document *</Label>
              <Input 
                value={uploadName} 
                onChange={(e) => setUploadName(e.target.value)} 
                placeholder="Ex: Contract cadru 2026"
                data-testid="upload-doc-name"
              />
            </div>
            <div>
              <Label>Categorie *</Label>
              <select 
                value={uploadCatId} 
                onChange={(e) => { setUploadCatId(e.target.value); setUploadSubId(''); }}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-md bg-white"
                data-testid="upload-doc-category"
              >
                <option value="">Selectează categorie</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            {uploadCatId && categories.find(c => c.id === uploadCatId)?.subcategories?.length > 0 && (
              <div>
                <Label>Subcategorie (opțional)</Label>
                <select 
                  value={uploadSubId} 
                  onChange={(e) => setUploadSubId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm border rounded-md bg-white"
                >
                  <option value="">Fără subcategorie</option>
                  {categories.find(c => c.id === uploadCatId)?.subcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label>Fișier *</Label>
              <Input 
                type="file" 
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="mt-1"
                data-testid="upload-doc-file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
              />
              {uploadFile && <p className="text-xs text-slate-500 mt-1">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Anulează</Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="upload-doc-submit"
              >
                {uploading ? 'Se încarcă...' : 'Încarcă'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === DIALOG: Access Control === */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Acces Utilizatori - {accessCategory?.name}</DialogTitle>
            <DialogDescription>
              Selectați utilizatorii care au acces la documentele din această categorie. 
              Dacă nu selectați niciunul, toți utilizatorii vor avea acces.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {accessUsers.length === 0 ? 'Acces: Toți utilizatorii' : `Acces: ${accessUsers.length} utilizator(i) selectat(i)`}
              </span>
              {accessUsers.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setAccessUsers([])}>
                  Resetează (toți)
                </Button>
              )}
            </div>
            <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
              {users.map(user => (
                <label key={user.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50">
                  <input 
                    type="checkbox" 
                    checked={accessUsers.includes(user.id)}
                    onChange={() => toggleUserAccess(user.id)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {user.nume && user.prenume ? `${user.nume} ${user.prenume}` : user.username}
                    </p>
                    <p className="text-xs text-slate-400">{user.username}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{user.role}</Badge>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAccessDialog(false)}>Anulează</Button>
              <Button onClick={handleSaveAccess} className="bg-blue-600 hover:bg-blue-700" data-testid="save-access-btn">
                Salvează Acces
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default DocumenteInterneTab;
